import React, { useState, useEffect } from 'react'
import {
    Box,
    Typography,
    IconButton,
    Chip,
    Button,
    Paper,
    TableContainer,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TextField,
    Modal,
    CircularProgress,
    LinearProgress,
} from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CheckIcon from '@mui/icons-material/Check'
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material'
import { UserContext } from '../../UserContext'
import {
    createApiToken,
    getApiTokens,
    deleteApiToken,
    getApiQuotaUsage,
} from '../../hooks/useApi'
import { useSnackbar } from '../../components/Snackbar'
import { useOnboardingStore } from '../../stores/useOnboardingStore'
import { useTheme } from '@mui/material/styles'
import { sendToAnalytics } from '../../Analytics'

const selectorShowSnackbar = state => state.showSnackbar

const formatDate = dateString => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' }
    return new Date(dateString).toLocaleDateString(undefined, options)
}

const formatResetTime = isoString => {
    if (!isoString) return null
    const resetDate = new Date(isoString)
    const now = new Date()
    const diffMs = resetDate - now
    if (diffMs <= 0) return 'resets when limit is reached'
    const timeStr = resetDate.toLocaleTimeString(undefined, {
        hour: '2-digit',
        minute: '2-digit',
    })
    const isToday = resetDate.toDateString() === now.toDateString()
    if (isToday) return `resets at ${timeStr}`
    const dateStr = resetDate.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
    })
    return `resets ${dateStr} at ${timeStr}`
}

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: theme => theme.palette.background.default,
    color: theme => theme.palette.text.primary,
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
}

export default function ApiTokenManager({ onKeyCreated } = {}) {
    const [copiedTokenIndex, setCopiedTokenIndex] = useState(null)
    const [tokenToDelete, setTokenToDelete] = useState(null)
    const [openDeleteModal, setOpenDeleteModal] = useState(false)
    const theme = useTheme()

    const [apiTokens, setApiTokens] = useState([])
    const [openCreateModal, setOpenCreateModal] = useState(false)
    const [newApiKeyName, setNewApiKeyName] = useState('')
    const [generatedToken, setGeneratedToken] = useState(null)
    const [loading, setLoading] = useState(true)
    const { id, username, email, plan, status } = React.useContext(UserContext)
    const [quotaUsage, setQuotaUsage] = useState(null)
    // use snack back from the our component
    const showSnackbar = useSnackbar(selectorShowSnackbar)
    const setApiKeyCount = useOnboardingStore(state => state.setApiKeyCount)

    const fetchApiTokens = async () => {
        if (!id) return

        try {
            setLoading(true)

            const userApiTokens = await getApiTokens(id)
            setApiTokens(userApiTokens)
        } catch (error) {
            showSnackbar('error', 'Failed to fetch API keys.')
            console.error('Error fetching API tokens:', error)
        } finally {
            setLoading(false)
        }
    }

    const fetchQuotaUsage = async () => {
        try {
            const data = await getApiQuotaUsage()
            if (data) setQuotaUsage(data)
        } catch (error) {
            console.error('Error fetching quota usage:', error)
        }
    }

    useEffect(() => {
        fetchApiTokens()
        fetchQuotaUsage()
    }, [id])

    const handleOpenCreateModal = () => setOpenCreateModal(true)
    const handleCloseCreateModal = () => setOpenCreateModal(false)

    const handleCreateApiKey = async () => {
        if (!newApiKeyName) {
            showSnackbar('warning', 'Please provide a name for the API key.')
            return
        }

        try {
            await fetchApiTokens()
            const tokenInfo = await createApiToken(id, newApiKeyName)
            setGeneratedToken(tokenInfo)
            if (onKeyCreated) onKeyCreated(newApiKeyName)
            // Analytics must never be mistaken for a key-creation failure: this
            // sits inside the handler's try, whose catch shows "Failed to create
            // API key."
            try {
                const analyticsData = {
                    source: 'api_access_page',
                    key_count: apiTokens.length + 1,
                }
                sendToAnalytics({
                    userId: username,
                    event: 'api_key_created',
                    data: analyticsData,
                })
                window.posthog?.capture?.('api_key_created', analyticsData)
            } catch (ignored) {}
            setNewApiKeyName('')
            handleCloseCreateModal()
            showSnackbar(
                'success',
                'API Key created successfully! Make sure to copy your personal access token now. You won’t be able to see it again!'
            )
        } catch (error) {
            showSnackbar('error', 'Failed to create API key.')
            console.error('Error creating API key:', error)
        }
    }

    const allApiTokens = [generatedToken, ...apiTokens].filter(
        token => token !== null
    )

    // This page is the only place that knows whether a key exists
    // so that what sets the store
    React.useEffect(() => {
        if (loading) return
        setApiKeyCount(allApiTokens.length)
    }, [loading, allApiTokens.length, setApiKeyCount])

    const handleCopyToken = (token, idx) => {
        navigator.clipboard.writeText(token)
        setCopiedTokenIndex(idx)
        setTimeout(() => setCopiedTokenIndex(null), 1500)
    }

    const handleTokenDeletion = async tokenId => {
        try {
            await deleteApiToken(tokenId)
            setApiTokens(prev => prev.filter(t => t.id !== tokenId))
            if (generatedToken && generatedToken.id === tokenId) {
                setGeneratedToken(null)
            }
            showSnackbar('success', 'API token deleted successfully')
        } catch (error) {
            showSnackbar('error', 'Failed to delete API token.')
            console.error('Error deleting API token:', error)
        } finally {
            setOpenDeleteModal(false)
            setTokenToDelete(null)
        }
    }

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                API Keys for user: {username}
            </Typography>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexDirection: 'row-reverse',
                    alignItems: 'center',
                    mb: 2,
                }}
            >
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleOpenCreateModal}
                    data-testid="api-create-key"
                >
                    Create API KEY
                </Button>
                {generatedToken && (
                    <Chip
                        label="Please copy your new API key. It will not be shown again."
                        color="success"
                    />
                )}
            </Box>

            {loading || !id ? (
                <CircularProgress />
            ) : (
                <TableContainer
                    component={Paper}
                    style={{
                        boxShadow: 'none',
                        backgroundColor: theme.palette.background.default,
                        mb: 6,
                    }}
                >
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>API Key</TableCell>
                                <TableCell>Created</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {allApiTokens.length > 0 ? (
                                allApiTokens.map((token, index) => (
                                    <TableRow key={index}>
                                        <TableCell>{token.name}</TableCell>
                                        <TableCell>
                                            {token.apiKey ? (
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                    }}
                                                >
                                                    <TextField
                                                        value={token.apiKey}
                                                        size="small"
                                                        variant="outlined"
                                                        InputProps={{
                                                            readOnly: true,
                                                        }}
                                                        sx={{
                                                            minWidth: 180,
                                                            mr: 1,
                                                        }}
                                                    />
                                                    <IconButton
                                                        size="small"
                                                        aria-label="Copy API token"
                                                        onClick={() =>
                                                            handleCopyToken(
                                                                token.apiKey,
                                                                index
                                                            )
                                                        }
                                                    >
                                                        {copiedTokenIndex ===
                                                        index ? (
                                                            <CheckIcon
                                                                color="success"
                                                                fontSize="small"
                                                            />
                                                        ) : (
                                                            <ContentCopyIcon fontSize="small" />
                                                        )}
                                                    </IconButton>
                                                </Box>
                                            ) : (
                                                '****'
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {formatDate(token.createdAt)}
                                        </TableCell>
                                        <TableCell>
                                            <IconButton
                                                onClick={() => {
                                                    setTokenToDelete(token)
                                                    setOpenDeleteModal(true)
                                                }}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={4} align="center">
                                        No API keys found.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Box sx={{ mt: 3, mb: 2 }}>
                <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ mb: 1.5 }}
                >
                    API Quota Usage (shared across all your API keys)
                </Typography>
                {quotaUsage?.usage && quotaUsage?.limits ? (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1.5,
                        }}
                    >
                        {quotaUsage.limits.hourlyAiLimit > 0 && (
                            <Box>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        mb: 0.5,
                                    }}
                                >
                                    <Typography variant="body2">
                                        AI Calls (hourly)
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {quotaUsage.usage.hourlyAiCalls || 0} /{' '}
                                        {quotaUsage.limits.hourlyAiLimit}
                                    </Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={Math.min(
                                        ((quotaUsage.usage.hourlyAiCalls || 0) /
                                            quotaUsage.limits.hourlyAiLimit) *
                                            100,
                                        100
                                    )}
                                    color={
                                        quotaUsage.status?.hourlyExceeded
                                            ? 'error'
                                            : 'primary'
                                    }
                                    sx={{ height: 6, borderRadius: 3 }}
                                />
                                {quotaUsage.resets?.hourlyResetsAt && (
                                    <Typography
                                        variant="caption"
                                        color="text.disabled"
                                        sx={{ mt: 0.25 }}
                                    >
                                        {formatResetTime(
                                            quotaUsage.resets.hourlyResetsAt
                                        )}
                                    </Typography>
                                )}
                            </Box>
                        )}
                        {quotaUsage.limits.weeklyApiLimit != null &&
                            quotaUsage.limits.weeklyApiLimit > 0 && (
                                <Box>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            mb: 0.5,
                                        }}
                                    >
                                        <Typography variant="body2">
                                            API Calls (weekly)
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            {quotaUsage.usage.weeklyApiCalls ||
                                                0}{' '}
                                            / {quotaUsage.limits.weeklyApiLimit}
                                        </Typography>
                                    </Box>
                                    <LinearProgress
                                        variant="determinate"
                                        value={Math.min(
                                            ((quotaUsage.usage.weeklyApiCalls ||
                                                0) /
                                                quotaUsage.limits
                                                    .weeklyApiLimit) *
                                                100,
                                            100
                                        )}
                                        color={
                                            quotaUsage.status?.weeklyExceeded
                                                ? 'error'
                                                : 'primary'
                                        }
                                        sx={{ height: 6, borderRadius: 3 }}
                                    />
                                    {quotaUsage.resets?.weeklyResetsAt && (
                                        <Typography
                                            variant="caption"
                                            color="text.disabled"
                                            sx={{ mt: 0.25 }}
                                        >
                                            {formatResetTime(
                                                quotaUsage.resets.weeklyResetsAt
                                            )}
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        {quotaUsage.limits.apiCallLimit > 0 && (
                            <Box>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        mb: 0.5,
                                    }}
                                >
                                    <Typography variant="body2">
                                        Total API Calls
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {quotaUsage.usage.totalApiCalls || 0} /{' '}
                                        {quotaUsage.limits.apiCallLimit}
                                    </Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={Math.min(
                                        ((quotaUsage.usage.totalApiCalls || 0) /
                                            quotaUsage.limits.apiCallLimit) *
                                            100,
                                        100
                                    )}
                                    color={
                                        quotaUsage.status?.totalExceeded
                                            ? 'error'
                                            : 'primary'
                                    }
                                    sx={{ height: 6, borderRadius: 3 }}
                                />
                            </Box>
                        )}
                    </Box>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        Quota usage data is not available yet. It will appear
                        here once you start using the API.
                    </Typography>
                )}
            </Box>

            <Modal open={openCreateModal} onClose={handleCloseCreateModal}>
                <Box sx={style}>
                    <Typography variant="h6" component="h2">
                        Create a new API Key
                    </Typography>
                    <Typography sx={{ my: 2 }}>
                        Please provide a name for your new API key.
                    </Typography>
                    <TextField
                        autoFocus
                        margin="dense"
                        id="name"
                        label="Your API Key Name"
                        type="text"
                        fullWidth
                        variant="standard"
                        value={newApiKeyName}
                        onChange={e => setNewApiKeyName(e.target.value)}
                    />
                    <Box
                        sx={{
                            mt: 3,
                            display: 'flex',
                            justifyContent: 'flex-end',
                        }}
                    >
                        <Button onClick={handleCloseCreateModal}>Cancel</Button>
                        <Button
                            onClick={handleCreateApiKey}
                            variant="contained"
                            sx={{ ml: 1 }}
                        >
                            Create
                        </Button>
                    </Box>
                </Box>
            </Modal>
            {/* Delete Confirmation Modal */}
            <Modal
                open={openDeleteModal}
                onClose={() => setOpenDeleteModal(false)}
            >
                <Box
                    sx={{
                        width: 360,
                        color: theme => theme.palette.text.primary,
                        ...style,
                    }}
                >
                    <Typography variant="h6" gutterBottom>
                        Revoke API Key
                    </Typography>
                    <Typography sx={{ mb: 3 }}>
                        Are you sure you want to revoke the API key
                        <b> {tokenToDelete?.name}</b>?
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: 2,
                        }}
                    >
                        <Button
                            onClick={() => setOpenDeleteModal(false)}
                            variant="outlined"
                        >
                            Cancel
                        </Button>
                        <Button
                            color="error"
                            variant="contained"
                            onClick={() => {
                                handleTokenDeletion(tokenToDelete.id)
                            }}
                        >
                            Delete
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </Box>
    )
}
